import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import OAuthLoginForm from '../../Auth/components/OAuthLoginForm';
import { trackCTA } from '../../../utils/metaPixel';

interface LandingAgentSignupModalProps {
  open: boolean;
  draftQuery: string;
  onClose: () => void;
}

const LandingAgentSignupModal: React.FC<LandingAgentSignupModalProps> = ({
  open,
  draftQuery,
  onClose,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && draftQuery.trim()) {
      sessionStorage.setItem('ggj_landing_agent_query', draftQuery.trim());
    }
  }, [open, draftQuery]);

  const handleAuthSuccess = useCallback(() => {
    trackCTA('landing_agent_modal_oauth_success');
    onClose();
  }, [onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="landing-agent-signup-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="landing-agent-signup-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
        >
          <button type="button" className="landing-agent-signup-modal__backdrop" onClick={onClose} aria-label={t('home.hero.agentDock.close')} />

          <motion.div
            className="landing-agent-signup-modal__panel"
            initial={{ opacity: 0, y: 20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
          >
            <button
              type="button"
              className="landing-agent-signup-modal__close"
              onClick={onClose}
              aria-label={t('home.hero.agentDock.close')}
            >
              <X className="h-4 w-4" />
            </button>

            <h2 id="landing-agent-signup-title" className="landing-agent-signup-modal__title">
              {t('home.hero.agentDock.modalTitle')}
            </h2>
            <p className="landing-agent-signup-modal__subtitle">{t('home.hero.agentDock.modalSubtitle')}</p>

            <div className="landing-agent-signup-modal__oauth">
              <OAuthLoginForm authMode="signup" onSuccess={handleAuthSuccess} />
            </div>

            <p className="landing-agent-signup-modal__legal">
              {t('home.hero.agentDock.termsPrefix')}{' '}
              <button type="button" onClick={() => window.open('/terms-of-service', '_blank', 'noopener,noreferrer')}>
                {t('home.hero.agentDock.terms')}
              </button>{' '}
              {t('home.hero.agentDock.and')}{' '}
              <button type="button" onClick={() => window.open('/privacy-policy', '_blank', 'noopener,noreferrer')}>
                {t('home.hero.agentDock.privacy')}
              </button>
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LandingAgentSignupModal;
